/**
 * Vendor disbursements — step 14. Structured like OwnerDisbursementRunPage,
 * adapted for the differences confirmed in CLAUDE.md's "Vendor
 * disbursements" section: no period picker (eligibility is a live "which
 * completed work orders haven't been paid yet" query, not scoped to a
 * calendar range), and staff pick a SUBSET of a vendor's eligible work
 * orders per approval rather than the owner flow's "one button pays
 * everything eligible."
 *
 * Approval is per-payout, every time — same "no approve all" discipline:
 * one Approve button per vendor, gated on at least one work order selected
 * plus the same per-row confirm checkbox before it fires a real Transfer.
 */

import { useEffect, useState } from 'react';
import { useInvalidate, useList } from '@refinedev/core';
import type { VendorDisbursementDto, VendorDisbursementRunPreviewDto } from 'shared';
import { api } from '../../../lib/api.js';
import { useCan } from '../../../lib/capabilities.js';
import { FormErrors, humanizeEnum } from '../../../components/Form.js';
import { DataTable, type Column } from '../../../components/DataTable.js';

function sumSelected(
  eligibleWorkOrders: VendorDisbursementRunPreviewDto['vendors'][number]['eligibleWorkOrders'],
  selected: Set<string>,
): string {
  const cents = eligibleWorkOrders
    .filter((wo) => selected.has(wo.workOrderId))
    .reduce((sum, wo) => sum + Math.round(Number(wo.cost) * 100), 0);
  return (cents / 100).toFixed(2);
}

function VendorRunRow({
  vendor,
  onApproved,
}: {
  vendor: VendorDisbursementRunPreviewDto['vendors'][number];
  onApproved: () => void;
}) {
  const canApprove = useCan('vendor-disbursements:approve');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmed, setConfirmed] = useState(false);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState<unknown>(null);

  function toggle(workOrderId: string) {
    setSelected((previous) => {
      const next = new Set(previous);
      if (next.has(workOrderId)) next.delete(workOrderId);
      else next.add(workOrderId);
      return next;
    });
    // A changed selection invalidates any prior confirmation — the number
    // Approve is about to fire against just changed.
    setConfirmed(false);
  }

  async function approve() {
    setApproving(true);
    setError(null);
    try {
      await api<VendorDisbursementDto>('/vendor-disbursements', {
        method: 'POST',
        body: JSON.stringify({ vendorId: vendor.vendorId, workOrderIds: [...selected] }),
      });
      onApproved();
    } catch (caught) {
      setError(caught);
    } finally {
      setApproving(false);
    }
  }

  const total = sumSelected(vendor.eligibleWorkOrders, selected);

  return (
    <div className="card stack">
      <div className="row">
        <strong>{vendor.vendorName}</strong>
        {vendor.willSettleViaConnect ? (
          <span
            className="pill pill--positive"
            title="Approving will Transfer and Payout via Stripe Connect"
          >
            via Connect
          </span>
        ) : (
          <span
            className="pill"
            title="Not enrolled — approving leaves this APPROVED, pending external settlement"
          >
            external settlement
          </span>
        )}
        <span className="shell__spacer" />
        <span className="muted">${total} selected</span>
      </div>

      <table className="table">
        <thead>
          <tr>
            <th></th>
            <th>Work order</th>
            <th>Property</th>
            <th>Completed</th>
            <th className="num">Cost</th>
          </tr>
        </thead>
        <tbody>
          {vendor.eligibleWorkOrders.map((wo) => (
            <tr key={wo.workOrderId}>
              <td>
                <input
                  type="checkbox"
                  checked={selected.has(wo.workOrderId)}
                  onChange={() => toggle(wo.workOrderId)}
                />
              </td>
              <td>{wo.title}</td>
              <td>
                {wo.propertyName}, {wo.unitLabel}
              </td>
              <td>{wo.completedAt.slice(0, 10)}</td>
              <td className="num">${wo.cost}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {error ? <FormErrors error={error} /> : null}

      {canApprove ? (
        <div className="row">
          <label className="row" style={{ gap: '0.25rem' }}>
            <input
              type="checkbox"
              checked={confirmed}
              disabled={selected.size === 0}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            <span className="muted" style={{ fontSize: '0.8rem' }}>
              Confirm ${total} to {vendor.vendorName}
            </span>
          </label>
          <button
            type="button"
            className="button"
            disabled={approving || selected.size === 0 || !confirmed}
            onClick={() => void approve()}
          >
            {approving ? 'Approving…' : 'Approve'}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function RunSection() {
  const [preview, setPreview] = useState<VendorDisbursementRunPreviewDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const invalidate = useInvalidate();

  async function loadPreview() {
    setLoading(true);
    setError(null);
    try {
      const result = await api<VendorDisbursementRunPreviewDto>('/vendor-disbursements/preview');
      setPreview(result);
    } catch (caught) {
      setError(caught);
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadPreview();
  }, []);

  async function onApproved() {
    await loadPreview();
    await invalidate({ resource: 'vendor-disbursements', invalidates: ['list'] });
  }

  return (
    <div className="stack">
      <div className="row">
        <h2>Eligible work orders</h2>
        <span className="shell__spacer" />
        <button
          type="button"
          className="button button--quiet"
          disabled={loading}
          onClick={() => void loadPreview()}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      <p className="muted">
        Completed work orders with a cost that hasn't been paid out yet, grouped by vendor. Nothing
        here is initiated until you approve a specific vendor's selection after clicking "Approve."
        This allows you time to check work quality before paying a vendor.
      </p>

      {error ? <FormErrors error={error} /> : null}

      {preview && preview.vendors.length === 0 ? (
        <p className="muted">No completed, unpaid work orders right now.</p>
      ) : null}

      {preview?.vendors.map((vendor) => (
        <VendorRunRow key={vendor.vendorId} vendor={vendor} onApproved={() => void onApproved()} />
      ))}
    </div>
  );
}

function SettleExternallyForm({
  disbursementId,
  vendorName,
  amount,
  onDone,
}: {
  disbursementId: string;
  vendorName: string;
  amount: string;
  onDone: () => void;
}) {
  const [referenceNumber, setReferenceNumber] = useState('');
  const [settledOn, setSettledOn] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function submit() {
    const reference = referenceNumber.trim();
    if (!reference) return;
    const confirmedSubmit = window.confirm(
      `Record a $${amount} external settlement for ${vendorName}, reference "${reference}", dated ${settledOn}?\n\n` +
        'This is a manual record — Stripe does not verify it, and it cannot be undone through the app.',
    );
    if (!confirmedSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/vendor-disbursements/${disbursementId}/settle-externally`, {
        method: 'POST',
        body: JSON.stringify({ referenceNumber: reference, settledOn }),
      });
      onDone();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="row">
      <input
        className="field__input"
        placeholder="Check / wire reference"
        value={referenceNumber}
        onChange={(event) => setReferenceNumber(event.target.value)}
      />
      <input
        className="field__input"
        type="date"
        value={settledOn}
        onChange={(event) => setSettledOn(event.target.value)}
      />
      <button
        type="button"
        className="button button--quiet"
        disabled={busy || !referenceNumber.trim()}
        onClick={() => void submit()}
      >
        {busy ? 'Recording…' : 'Record settlement'}
      </button>
      {error ? <FormErrors error={error} /> : null}
    </div>
  );
}

function RetryPayoutButton({
  disbursementId,
  onDone,
}: {
  disbursementId: string;
  onDone: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function retry() {
    setBusy(true);
    setError(null);
    try {
      await api(`/vendor-disbursements/${disbursementId}/retry-payout`, { method: 'POST' });
      onDone();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack" style={{ gap: '0.2rem' }}>
      <button
        type="button"
        className="button button--quiet"
        disabled={busy}
        onClick={() => void retry()}
      >
        {busy ? 'Retrying…' : 'Retry payout'}
      </button>
      {error ? <FormErrors error={error} /> : null}
    </div>
  );
}

function ListSection() {
  const canApprove = useCan('vendor-disbursements:approve');
  const invalidate = useInvalidate();
  const [openId, setOpenId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data, isLoading, error } = useList<VendorDisbursementDto>({
    resource: 'vendor-disbursements',
    pagination: { mode: 'off' },
  });

  function refresh() {
    setOpenId(null);
    void invalidate({ resource: 'vendor-disbursements', invalidates: ['list'] });
  }

  const columns: Column<VendorDisbursementDto>[] = [
    { key: 'vendor', header: 'Vendor', render: (row) => row.vendorName },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <div className="stack" style={{ gap: '0.15rem' }}>
          <span
            className={`pill${row.status === 'PAYOUT_FAILED' ? ' pill--negative' : row.status === 'PAID' ? ' pill--positive' : ''}`}
          >
            {humanizeEnum(row.status)}
          </span>
          {row.transferredAt ? (
            <span className="muted" style={{ fontSize: '0.75rem' }}>
              Transferred {row.transferredAt.slice(0, 10)}
            </span>
          ) : null}
          {row.paidAt ? (
            <span className="muted" style={{ fontSize: '0.75rem' }}>
              Paid {row.paidAt.slice(0, 10)}
            </span>
          ) : null}
          {row.payoutFailureReason ? (
            <span className="muted" style={{ fontSize: '0.75rem' }}>
              {row.payoutFailureReason}
              {row.payoutAttemptCount > 0 ? ` (attempt ${String(row.payoutAttemptCount)})` : ''}
            </span>
          ) : null}
        </div>
      ),
    },
    { key: 'amount', header: 'Amount', numeric: true, render: (row) => `$${row.amount}` },
    {
      key: 'lines',
      header: 'Work orders',
      render: (row) => (
        <button
          type="button"
          className="button button--quiet"
          onClick={() => setExpandedId((id) => (id === row.id ? null : row.id))}
        >
          {expandedId === row.id ? 'Hide' : `${String(row.lines.length)} work order(s)`}
        </button>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (row) => {
        if (!canApprove) return null;
        if (row.status === 'PAYOUT_FAILED') {
          return <RetryPayoutButton disbursementId={row.id} onDone={refresh} />;
        }
        if (row.status === 'APPROVED') {
          return openId === row.id ? (
            <SettleExternallyForm
              disbursementId={row.id}
              vendorName={row.vendorName}
              amount={row.amount}
              onDone={refresh}
            />
          ) : (
            <button
              type="button"
              className="button button--quiet"
              onClick={() => setOpenId(row.id)}
            >
              Record external settlement
            </button>
          );
        }
        return null;
      },
    },
  ];

  const expandedRow = (data?.data ?? []).find((row) => row.id === expandedId);

  return (
    <div className="card stack">
      <h2>All disbursements</h2>
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        error={error}
        emptyMessage="No disbursements approved yet."
      />
      {expandedRow ? (
        <table className="table">
          <thead>
            <tr>
              <th>Work order</th>
              <th>Property</th>
              <th className="num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {expandedRow.lines.map((line) => (
              <tr key={line.id}>
                <td>{line.workOrderTitle}</td>
                <td>
                  {line.propertyName}, {line.unitLabel}
                </td>
                <td className="num">${line.amount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

export function VendorDisbursementRunPage() {
  return (
    <section className="stack">
      <h1>Vendor Disbursements</h1>
      <p className="muted">
        Approving an enrolled vendor's selection triggers a real Stripe Transfer then Payout. A
        vendor not enrolled in Connect displays here, but can only be paid through staff-recorded
        external settlements. Vendors receive the full completed-work-order cost.
      </p>
      <RunSection />
      <ListSection />
    </section>
  );
}
